import { useTranslation } from 'react-i18next'

import { Insights } from '@percent/domain/insights'
import { InsightsAiMetricResult } from '@percent/partner-dashboard/services/insightsAi/insightsAiService.types'
import classes from './InsightsAiResults.module.scss'

/**
 * One metric result, handed to the shared insights chart, which renders the `metricData`
 * natively and presents it as the agent's `suggestedChart` hint (validated against the data shape
 * by the chart, which falls back to its shape-based auto pick — scalar -> stat tile, breakdown ->
 * bar/donut, timeseries -> line — when the hint is absent or doesn't fit). (Results the agent
 * could not compute are never emitted — they're explained in the answer prose instead.)
 *
 * Every result is an equal-width card in the answer's grid, per the design. The legend is dropped
 * only where the chart self-labels: a timeseries reads its series off the axis, so the legend is
 * redundant at this width; a `rows` breakdown (donut / category bar) keeps its legend, which is
 * the only labelling it has.
 */
export function InsightsAiResult({ result }: Readonly<{ result: InsightsAiMetricResult }>) {
  const { t } = useTranslation()
  const isScalar = result.metricData.type === 'scalar'
  const isSelfLabelling = 'structure' in result.metricData && result.metricData.structure === 'timeseries'
  // `none` means the value lives in the prose with no card, so it's not a chart hint — drop it to
  // `undefined` (the chart then auto-picks from the shape, exactly as it does for an unknown hint).
  // Every other value, including `table`, is a real hint the shared chart understands.
  const { suggestedChart } = result.metricData
  const chartHint = suggestedChart === 'none' ? undefined : suggestedChart

  return (
    <div className={isScalar ? classes.scalarResult : undefined}>
      <Insights.MetricChart
        data={result.metricData}
        title={result.title}
        subtitle={result.subtitle}
        suggestedChart={chartHint}
        showLegend={!isSelfLabelling}
        tableLabels={{
          value: t('typography.dashboard.insightsAi.table.value'),
          total: t('typography.dashboard.insightsAi.table.total'),
          share: t('typography.dashboard.insightsAi.table.share')
        }}
        testId={`insights-ai-result-${result.ref}`}
      />
    </div>
  )
}
